import React, { useEffect, useState } from "react";
import { Col, Row, Tabs, Tab } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
  setCurrentPageTitle,
  setCurrentRoute,
} from "../app/store/layoutUI";

// Components
import AgencyInvoiceBased from "../app/components/Reports/Monthly/Financial/AgencyInvoiceBased";
import AgencyCandidateBased from "../app/components/Reports/Monthly/Financial/AgencyCandidateBased";
import SubcontractorCandidates from "../app/components/Reports/Monthly/Payment/SubcontractorCandidates";
import RecruiterCandidates from "../app/components/Reports/Monthly/Payment/RecruiterCandidates";
import AgencyInvoiceCandidate from "../app/components/Reports/Monthly/AgencyInvoiceCandidate";
import GrossProfitCandidates from "../app/components/Reports/Monthly/GrossProfitCandidates";
import BurnHourCandidate from "../app/components/Reports/Monthly/BurnHourCandidate";
import AgencyInvBased from "../app/components/Reports/Quarterly/Financial/AgencyInvBased";
import AgencyCanBased from "../app/components/Reports/Quarterly/Financial/AgencyCanBased";
import AgencyInvoicePayment from "../app/components/Reports/Other/AgencyInvoicePayment";
import SubcontractorComp from "../app/components/Reports/Compliance/SubcontractorComp";
import RecruiterComp from "../app/components/Reports/Compliance/RecruiterComp";
import W2CandidateComp from "../app/components/Reports/Compliance/W2CandidateComp";
import Ten99CandidateComp from "../app/components/Reports/Compliance/Ten99CandidateComp";
import BurnHrCandidate from "../app/components/Reports/Quarterly/BurnHrCandidate";
import AchInformation from "../app/components/Reports/Other/AchInformation";
import InsuranceInformation from "../app/components/Reports/Other/InsuranceInformation";
import CandidateVisaStatus from "../app/components/Reports/Other/CandidateVisaStatus";
import SubcontractorRecruiter from "../app/components/Reports/Monthly/Ach/SubcontractorRecruiter";

const CustomReport = () => {
  const dispatch = useDispatch();
  const { type } = useParams();

  const [selectedReport, setSelectedReport] = useState("Select Report");
  const [selectedCompliance, setSelectedCompliance] = useState("Select the Entity to send the reports to");
  const [activeKey, setActiveKey] = useState("internal");
  const [searchTerm, setSearchTerm] = useState("");

  // ------------------------------
  // Report / Compliance definitions with UI metadata
  // ------------------------------
  const reportOptions = [
    {
      name: "ACH Information Reports",
      description: "Overview of ACH banking details for active performers and vendor partners.",
      icon: "fa-university",
      color: "rgba(99, 102, 241, 0.08)",
      iconColor: "#4f46e5",
      component: <AchInformation />
    },
    {
      name: "Insurance Information Reports",
      description: "Summary of general liability insurance coverage and active policies.",
      icon: "fa-shield",
      color: "rgba(14, 165, 233, 0.08)",
      iconColor: "#0ea5e9",
      component: <InsuranceInformation />
    },
    {
      name: "Candidate Visa Status Reports",
      description: "Track candidate work eligibility, visa types, and expiration statuses.",
      icon: "fa-id-card-o",
      color: "rgba(16, 185, 129, 0.08)",
      iconColor: "#10b981",
      component: <CandidateVisaStatus />
    },
    {
      name: "Monthly ACH - Payments",
      description: "Review automated clearing house (ACH) payouts for subcontractors and recruiters.",
      icon: "fa-money",
      color: "rgba(245, 158, 11, 0.08)",
      iconColor: "#f59e0b",
      component: <SubcontractorRecruiter />
    },
    {
      name: "Financial Report - (Accrual Basis - Invoice Based)",
      description: "Accrual basis billing insights based on generated client invoices.",
      icon: "fa-line-chart",
      color: "rgba(239, 68, 68, 0.08)",
      iconColor: "#ef4444",
      component: <AgencyInvoiceBased />
    },
    {
      name: "Financial Report - (Accrual Basis - Candidate Based)",
      description: "Accrual basis reports categorized by candidate earnings.",
      icon: "fa-bar-chart",
      color: "rgba(139, 92, 246, 0.08)",
      iconColor: "#8b5cf6",
      component: <AgencyCandidateBased />
    },
    {
      name: "Monthly Payments (Per Candidate Basis)",
      description: "Monthly payout structures organized by individual candidates.",
      icon: "fa-calculator",
      color: "rgba(6, 182, 212, 0.08)",
      iconColor: "#06b6d4",
      component: <SubcontractorCandidates />
    },
    {
      name: "Monthly Invoices to Client (Per Candidate-Basis)",
      description: "Client billing tracking grouped per candidate engagement.",
      icon: "fa-file-text-o",
      color: "rgba(236, 72, 153, 0.08)",
      iconColor: "#ec4899",
      component: <AgencyInvoiceCandidate />
    },
    {
      name: "Monthly Gross Profit Reports - Per Candidate Basis",
      description: "Check net profit yield and margins generated per candidate placement.",
      icon: "fa-pie-chart",
      color: "rgba(20, 184, 166, 0.08)",
      iconColor: "#14b8a6",
      component: <GrossProfitCandidates />
    },
    {
      name: "Burn Reports - Per Candidate Basis",
      description: "Hourly burn rates, project limits, and candidate engagement lifetimes.",
      icon: "fa-fire",
      color: "rgba(244, 63, 94, 0.08)",
      iconColor: "#f43f5e",
      component: <BurnHourCandidate />
    }
  ];

  const complianceOptions = [
    {
      name: "Subcontractors",
      description: "Enrollment tracking, state certifications, and subcontractor validation docs.",
      icon: "fa-handshake-o",
      color: "rgba(245, 158, 11, 0.08)",
      iconColor: "#f59e0b",
      component: <SubcontractorComp />
    },
    {
      name: "Recruiters",
      description: "Recruiter registration forms, identity validations, and compliance checklists.",
      icon: "fa-users",
      color: "rgba(239, 68, 68, 0.08)",
      iconColor: "#ef4444",
      component: <RecruiterComp />
    },
    {
      name: "W2 Candidates",
      description: "Employment tax documents, payroll registrations, and W2 identity forms.",
      icon: "fa-file-text",
      color: "rgba(99, 102, 241, 0.08)",
      iconColor: "#4f46e5",
      component: <W2CandidateComp />
    },
    {
      name: "1099 Candidates",
      description: "Independent contractor declarations, classifications, and tax logs.",
      icon: "fa-briefcase",
      color: "rgba(16, 185, 129, 0.08)",
      iconColor: "#10b981",
      component: <Ten99CandidateComp />
    }
  ];

  useEffect(() => {
    const pageTitle = type === "compliance" ? "Compliance Reports/Docs" : "Business Reports";
    dispatch(setCurrentPageTitle(pageTitle));
    dispatch(setCurrentRoute([{ name: "Analytics", url: "" }]));
    
    // Reset selections on path change
    setSelectedReport("Select Report");
    setSelectedCompliance("Select the Entity to send the reports to");
    setSearchTerm("");
  }, [type, dispatch]);

  const handleTabSelect = (k) => {
    setActiveKey(k);
    setSelectedReport("Select Report");
    setSearchTerm("");
  };

  // ------------------------------
  // Catalog Rendering logic
  // ------------------------------
  const renderCatalogGrid = (options, selectedValue, onSelect, placeholderText) => {
    const filtered = options.filter(
      (opt) =>
        opt.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        opt.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <React.Fragment>
        <div className="cr-search-wrapper">
          <i className="fa fa-search cr-search-icon"></i>
          <input
            type="text"
            placeholder={placeholderText}
            className="cr-search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {filtered.length > 0 ? (
          <div className="cr-grid">
            {filtered.map((opt) => (
              <div className="cr-card" key={opt.name} onClick={() => onSelect(opt.name)}>
                <div className="cr-icon-wrapper" style={{ background: opt.color, color: opt.iconColor }}>
                  <i className={`fa ${opt.icon}`}></i>
                </div>
                <h4 className="cr-card-title">{opt.name}</h4>
                <p className="cr-card-desc">{opt.description}</p>
                <div className="cr-card-footer">
                  <span>Run Report</span>
                  <i className="fa fa-chevron-right"></i>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-5 text-muted bg-white border rounded" style={{ borderRadius: "12px" }}>
            <i className="fa fa-folder-open-o fa-2x mb-2 d-block"></i>
            <small>No reports matched your filter criteria</small>
          </div>
        )}
      </React.Fragment>
    );
  };

  const renderActiveReportView = (options, selectedValue, onBack, breadcrumbCategory) => {
    const match = options.find((item) => item.name === selectedValue);
    if (!match) return null;

    return (
      <React.Fragment>
        <div className="cr-header">
          <div className="cr-breadcrumb">
            <span>Reports</span>
            <i className="fa fa-angle-right text-muted"></i>
            <span>{breadcrumbCategory}</span>
            <i className="fa fa-angle-right text-muted"></i>
            <span className="active-node">{selectedValue}</span>
          </div>
          <button className="cr-btn-back" onClick={onBack}>
            <i className="fa fa-arrow-left"></i> Back to Directory
          </button>
        </div>
        <div className="animate-fade-in bg-white p-3 border rounded shadow-sm" style={{ borderRadius: "16px" }}>
          {match.component}
        </div>
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <style>{`
        /* Modern Stylesheet for CustomReport Hub */
        .cr-hub {
          font-family: 'Inter', 'Open Sans', system-ui, -apple-system, sans-serif;
          background: #f8fafc;
          min-height: calc(100vh - 120px);
          padding: 8px 0;
        }

        .cr-title {
          font-size: 22px;
          font-weight: 800;
          color: #0f172a;
          letter-spacing: -0.02em;
        }

        .cr-subtitle {
          font-size: 13.5px;
          color: #64748b;
        }

        /* Search input bar styling */
        .cr-search-wrapper {
          position: relative;
          max-width: 400px;
          margin-bottom: 24px;
        }

        .cr-search-input {
          width: 100%;
          padding: 10px 16px 10px 38px;
          background: #ffffff;
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          font-size: 13.5px;
          color: #1e293b;
          transition: all 0.2s ease;
        }

        .cr-search-input:focus {
          outline: none;
          border-color: #4f46e5;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.15);
        }

        .cr-search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
          font-size: 13px;
        }

        /* Catalog Grid Layout */
        .cr-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .cr-card {
          background: #ffffff;
          border: 1px solid rgba(226, 232, 240, 0.8);
          border-radius: 16px;
          padding: 24px;
          display: flex;
          flex-direction: column;
          box-shadow: 0 4px 6px -1px rgba(0,0,0,0.02), 0 2px 4px -1px rgba(0,0,0,0.01);
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
          height: 100%;
        }

        .cr-card:hover {
          transform: translateY(-3px);
          border-color: rgba(79, 70, 229, 0.25);
          box-shadow: 0 12px 20px -3px rgba(79, 70, 229, 0.08), 0 4px 6px -2px rgba(0,0,0,0.02);
        }

        .cr-icon-wrapper {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 18px;
          margin-bottom: 18px;
          transition: all 0.2s ease;
          box-shadow: inset 0 -2px 0 rgba(0,0,0,0.05);
        }

        .cr-card:hover .cr-icon-wrapper {
          transform: scale(1.08);
        }

        .cr-card-title {
          font-size: 15px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 8px;
          line-height: 1.35;
        }

        .cr-card-desc {
          font-size: 12px;
          color: #64748b;
          line-height: 1.5;
          margin-bottom: 20px;
          flex-grow: 1;
        }

        .cr-card-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 12.5px;
          font-weight: 700;
          color: #4f46e5;
          border-top: 1px solid #f1f5f9;
          padding-top: 14px;
          margin-top: auto;
          transition: all 0.15s ease;
        }

        .cr-card:hover .cr-card-footer {
          color: #4338ca;
        }

        /* Active view header styles */
        .cr-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: #ffffff;
          border: 1px solid rgba(226, 232, 240, 0.8);
          border-radius: 14px;
          padding: 14px 20px;
          margin-bottom: 24px;
          box-shadow: 0 2px 4px -1px rgba(0,0,0,0.01);
        }

        .cr-breadcrumb {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13.5px;
          font-weight: 600;
          color: #64748b;
        }

        .cr-breadcrumb .active-node {
          color: #0f172a;
          font-weight: 750;
        }

        .cr-btn-back {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 7px 14px;
          font-size: 12.5px;
          font-weight: 600;
          border-radius: 8px;
          border: 1px solid #cbd5e1;
          background: #ffffff;
          color: #475569;
          cursor: pointer;
          transition: all 0.15s ease;
        }

        .cr-btn-back:hover {
          background: #f8fafc;
          color: #0f172a;
          border-color: #94a3b8;
        }

        /* Tabs customization styling overrides */
        .cr-tabs .nav-pills {
          border-bottom: 1px solid #e2e8f0;
          padding-bottom: 8px;
          margin-bottom: 24px;
          display: flex;
          gap: 6px;
        }

        .cr-tabs .nav-item {
          margin: 0;
        }

        .cr-tabs .nav-link {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          color: #64748b;
          font-weight: 600;
          font-size: 13.5px;
          border-radius: 10px;
          padding: 10px 18px;
          transition: all 0.15s ease;
        }

        .cr-tabs .nav-link:hover {
          background: #f1f5f9;
          color: #334155;
          border-color: #cbd5e1;
        }

        .cr-tabs .nav-link.active {
          background: #4f46e5 !important;
          color: #ffffff !important;
          border-color: #4f46e5;
          box-shadow: 0 4px 12px rgba(79, 70, 229, 0.18);
        }

        .cr-hero-header {
          background: linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%);
          border: 1px solid rgba(15, 23, 42, 0.08);
          border-radius: 18px;
          color: #1e293b;
          margin-bottom: 24px;
          padding: 30px 32px 26px;
          position: relative;
          overflow: hidden;
          box-shadow:
            0 1px 3px rgba(15, 23, 42, 0.02),
            0 8px 24px rgba(15, 23, 42, 0.04),
            inset 0 1px 0 #ffffff;
        }

        .cr-hero-kicker {
          color: #4f46e5;
          font-size: 11px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 8px;
        }

        .cr-hero-title {
          font-size: 28px;
          font-weight: 800;
          color: #0f172a;
          letter-spacing: -0.02em;
          margin-bottom: 10px;
          line-height: 1.2;
        }

        .cr-hero-copy {
          font-size: 13.5px;
          color: #475569;
          line-height: 1.6;
          margin-bottom: 0;
          max-width: 720px;
        }

        .cr-tab-hero-header {
          background: linear-gradient(to right, #f8fafc 0%, #ffffff 100%);
          border: 1px solid #e2e8f0;
          border-left: 4px solid #4f46e5;
          border-radius: 8px;
          padding: 16px 24px;
          margin-bottom: 20px;
          position: relative;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
          overflow: hidden;
        }

        .cr-tab-hero-header.tab-internal {
          border-left-color: #4f46e5;
        }

        .cr-tab-hero-header.tab-agencies {
          border-left-color: #0ea5e9;
        }

        .cr-tab-hero-kicker {
          color: #4f46e5;
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 4px;
        }

        .cr-tab-hero-header.tab-agencies .cr-tab-hero-kicker {
          color: #0ea5e9;
        }

        .cr-tab-hero-title {
          font-size: 18px;
          font-weight: 700;
          color: #0f172a;
          margin-bottom: 6px;
        }

        .cr-tab-hero-copy {
          font-size: 12px;
          color: #64748b;
          line-height: 1.5;
          margin-bottom: 0;
          max-width: 80%;
        }

        .cr-tab-hero-icon {
          position: absolute;
          right: 24px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 44px;
          color: #4f46e5;
          opacity: 0.06;
          pointer-events: none;
        }

        .cr-tab-hero-header.tab-agencies .cr-tab-hero-icon {
          color: #0ea5e9;
        }
      `}</style>

      <div className="cr-hub">
        {type !== "compliance" && (
          <div className="cr-hero-header">
            <div className="cr-hero-kicker">BUSINESS INTELLIGENCE HUB</div>
            <h1 className="cr-hero-title">Business Intelligence Hub</h1>
            <p className="cr-hero-copy">
              Review accrual reports, invoices, ACH payments, profit distributions, and workforce burns.
            </p>
          </div>
        )}

        {type === "compliance" ? (
          /* ✅ COMPLIANCE HUB */
          <div className="cr-tabs">
            <div className="cr-hero-header">
              <div className="cr-hero-kicker">COMPLIANCE AUDIT CENTER</div>
              <h1 className="cr-hero-title">Compliance Audit Center</h1>
              <p className="cr-hero-copy">
                Assess recruitment, subcontractor, and candidate onboarding compliance forms and logs.
              </p>
            </div>
            {selectedCompliance === "Select the Entity to send the reports to" ? (
              /* Catalog Grid Mode */
              renderCatalogGrid(
                complianceOptions,
                selectedCompliance,
                setSelectedCompliance,
                "Search compliance logs (Subcontractors, W2, Recruiters)..."
              )
            ) : (
              /* Active Report Mode */
              renderActiveReportView(
                complianceOptions,
                selectedCompliance,
                () => setSelectedCompliance("Select the Entity to send the reports to"),
                "Compliance Audits"
              )
            )}
          </div>
        ) : (
          /* ✅ BUSINESS HUB */
          <div className="cr-tabs">
            <Tabs
              id="business-tabs"
              activeKey={activeKey}
              onSelect={handleTabSelect}
              variant="pills"
            >
              {/* Internal Tab */}
              <Tab eventKey="internal" title="Internal Reports" tabClassName="m-0">
                <div className="cr-tab-hero-header tab-internal">
                  <div className="cr-tab-hero-kicker">INTERNAL REPORTS</div>
                  <h2 className="cr-tab-hero-title">Internal Business Reports</h2>
                  <p className="cr-tab-hero-copy">
                    Track candidate payouts, payroll schedules, recruiter fee structures, profit distributions, and burn hours.
                  </p>
                  <i className="fa fa-file-text-o cr-tab-hero-icon"></i>
                </div>
                {selectedReport === "Select Report" ? (
                  /* Catalog Grid Mode */
                  renderCatalogGrid(
                    reportOptions,
                    selectedReport,
                    setSelectedReport,
                    "Search business reports (ACH, Visa, Gross Profit, Burn)..."
                  )
                ) : (
                  /* Active Report Mode */
                  renderActiveReportView(
                    reportOptions,
                    selectedReport,
                    () => setSelectedReport("Select Report"),
                    "Internal Business"
                  )
                )}
              </Tab>

              {/* Agencies Tab */}
              <Tab
                eventKey="agencies"
                title="Agencies Ledger (Monthly/Quarterly/Yearly)"
                tabClassName="m-0"
              >
                <div className="cr-tab-hero-header tab-agencies">
                  <div className="cr-tab-hero-kicker">AGENCIES LEDGER</div>
                  <h2 className="cr-tab-hero-title">Agency Invoicing & Payments Ledger</h2>
                  <p className="cr-tab-hero-copy">
                    Unified statement of invoices, performer payments, tech margins, and agency fees.
                  </p>
                  <i className="fa fa-university cr-tab-hero-icon"></i>
                </div>

                <div className="animate-fade-in bg-white p-3 border rounded shadow-sm" style={{ borderRadius: "16px" }}>
                  {activeKey === "agencies" && <AgencyInvoicePayment />}
                </div>
              </Tab>
            </Tabs>
          </div>
        )}
      </div>
    </React.Fragment>
  );
};

export default CustomReport;
